<template>
  <v-dialog v-model="open" max-width="540" persistent @keydown.esc="onCancel">
    <v-card style="border-radius:16px; overflow:hidden;">

      <!-- Header -->
      <div class="modal-header">
        <v-icon color="#00BCD4" size="22" class="mr-2">mdi-account-search-outline</v-icon>
        <span class="modal-title">Odabir kupca</span>
        <v-spacer />
        <button class="modal-close" @click="onCancel">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">

        <!-- Search pill -->
        <div class="search-pill" :class="{ focused: inputFocused }">
          <v-icon size="16" color="#00BCD4" class="mr-2">mdi-magnify</v-icon>
          <input
            ref="searchInput"
            v-model="query"
            class="search-input"
            placeholder="OIB ili naziv kupca..."
            autocomplete="off"
            @input="onInput"
            @keydown.enter="doSearch"
            @focus="inputFocused = true"
            @blur="inputFocused = false"
          />
          <v-progress-circular v-if="loading" indeterminate color="#00BCD4" size="14" width="2" class="ml-1" />
          <button v-if="query" class="search-clear" @click.stop="clearSearch">
            <v-icon size="13">mdi-close</v-icon>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="search-loading">
          <v-progress-circular indeterminate color="#00BCD4" size="28" width="2" />
          <span>Pretraživanje...</span>
        </div>

        <!-- Rezultati pretrage -->
        <div v-else-if="results.length" class="results-list">
          <div
            v-for="r in results"
            :key="r.id || (r.oibs && r.oibs[0]) || r.name"
            class="result-row"
            :class="{ selected: form.name === r.name }"
            @click="selectResult(r)"
          >
            <v-icon size="15" color="#00BCD4" class="mr-2">mdi-account-outline</v-icon>
            <div class="result-info">
              <div class="result-name">{{ r.name }}</div>
              <div class="result-oib">
                {{ r.oib || (r.oibs && r.oibs[0]) || '' }}
                <span v-if="r.city"> · {{ r.city }}</span>
              </div>
            </div>
            <v-icon v-if="form.name === r.name" size="16" color="#00BCD4">mdi-check-circle</v-icon>
          </div>
        </div>

        <div v-else-if="!loading && searched && query.length >= 3" class="search-empty">
          <v-icon size="32" color="#E0E6ED">mdi-account-search-outline</v-icon>
          <span>Nema rezultata za "{{ query }}"</span>
        </div>
        <div v-else-if="!loading && !searched && !hasForm" class="search-hint">
          <v-icon size="24" color="#E0E6ED" class="mb-1">mdi-magnify</v-icon>
          <span>Upiši min. 3 znaka za pretragu</span>
        </div>

        <!-- Polja kupca — uvijek vidljiva, popune se odabirom -->
        <div class="customer-fields">
          <div class="fields-header">
            <span class="fields-title">Podaci kupca</span>
            <button v-if="hasForm" class="fields-clear" @click="clearForm" title="Ukloni kupca">
              <v-icon size="14" color="#E53935">mdi-delete-outline</v-icon>
              <span>Ukloni</span>
            </button>
          </div>

          <v-row dense>
            <v-col cols="8">
              <v-text-field
                v-model="form.name"
                label="Naziv / Ime i prezime"
                outlined dense hide-details
                class="field-input"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="form.oib"
                label="OIB"
                outlined dense
                :error-messages="oibError ? [oibError] : []"
                hide-details="auto"
                class="field-input"
              />
            </v-col>
          </v-row>

          <v-row dense class="mt-2">
            <v-col cols="12">
              <v-text-field
                v-model="form.address"
                label="Adresa"
                outlined dense hide-details
                class="field-input"
              />
            </v-col>
          </v-row>

          <v-row dense class="mt-2">
            <v-col cols="4">
              <v-text-field
                v-model="form.zip_code"
                label="Poštanski broj"
                outlined dense hide-details
                class="field-input"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="form.city"
                label="Grad"
                outlined dense hide-details
                class="field-input"
              />
            </v-col>
            <v-col cols="4">
              <v-combobox
                v-model="form.country"
                :items="countryList"
                label="Država"
                outlined dense hide-details
                class="field-input"
              />
            </v-col>
          </v-row>
        </div>

          <!-- Spremi u bazu -->
          <div class="save-row mt-3">
            <v-checkbox
              v-model="saveToDatabase"
              dense hide-details
              color="#00BCD4"
              class="save-checkbox"
            >
              <template v-slot:label>
                <span class="save-label">Spremi kupca u bazu podataka</span>
              </template>
            </v-checkbox>
          </div>

        </div>

      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn-cancel" @click="onCancel">Odustani</button>
        <button class="btn-confirm" :disabled="saving || !!oibError" @click="onConfirm">
          <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" class="mr-1" />
          <v-icon v-else size="16" color="white" class="mr-1">mdi-check</v-icon>
          {{ saving ? 'Sprema...' : 'Potvrdi' }}
        </button>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import state from '@/state'
import { callAssociates, callBusinessEntities } from '@/api/associates'
import rules from '@/plugins/rules'

const EMPTY_FORM = () => ({ name: '', oib: '', address: '', zip_code: '', city: '', country: 'Hrvatska', email: '' })

export default {
  name: 'ShopCustomerPanel',

  props: {
    open: { type: Boolean, default: false },
    currentCustomer: { type: Object, default: null }
  },

  data () {
    return {
      query: '',
      results: [],
      loading: false,
      searched: false,
      inputFocused: false,
      debounceTimer: null,
      form: EMPTY_FORM(),
      saveToDatabase: false,
      saving: false,
      countryList: ['Hrvatska', 'Slovenija', 'Bosna i Hercegovina', 'Srbija', 'Crna Gora', 'Austrija', 'Njemačka', 'Italija', 'Mađarska', 'Češka', 'Slovačka', 'Poljska', 'Francuska', 'Španjolska', 'Ujedinjeno Kraljevstvo']
    }
  },

  computed: {
    hasForm () {
      return !!(this.form.name || this.form.oib)
    },

    oibError () {
      if (this.form.country !== 'Hrvatska' || !this.form.oib) return ''
      const result = rules.oib()(this.form.oib.trim())
      return result === true ? '' : result
    }
  },

  watch: {
    open (v) {
      if (v) {
        this.query = ''
        this.results = []
        this.searched = false
        if (this.currentCustomer) {
          this.fillForm(this.currentCustomer)
        } else {
          this.form = EMPTY_FORM()
        }
        setTimeout(() => {
          this.$refs.searchInput && this.$refs.searchInput.focus()
        }, 150)
      }
    }
  },

  methods: {
    fillForm (c) {
      this.form = {
        name: c.name || '',
        oib: c.oib || (c.oibs && c.oibs[0]) || '',
        address: c.address || '',
        zip_code: c.zip_code || '',
        city: c.city || '',
        country: c.country || 'Hrvatska',
        email: c.email || ''
      }
    },

    clearForm () {
      this.form = EMPTY_FORM()
      this.results = []
      this.query = ''
      this.searched = false
    },

    clearSearch () {
      this.query = ''
      this.results = []
      this.searched = false
      this.$refs.searchInput && this.$refs.searchInput.focus()
    },

    onInput () {
      clearTimeout(this.debounceTimer)
      if (this.query.length < 3) {
        this.results = []
        this.searched = false
        return
      }
      this.debounceTimer = setTimeout(() => this.doSearch(), 300)
    },

    async doSearch () {
      if (this.query.length < 3) return
      this.loading = true
      this.searched = false
      try {
        const q = this.query.trim()
        const isOib = /^\d+$/.test(q)
        const path = `/associates/${state.getCurrentCompany().id}/associate?filter=trigram(eq:${encodeURIComponent(q)})`
        const response = await callAssociates('get', path)

        if (response && response.status === 200 && response.data?.associates) {
          const list = Object.keys(response.data.associates)
            .map(k => response.data.associates[k])
            .filter(a => a.status === 'OK')

          if (isOib) {
            const flat = []
            list.forEach(a => {
              const oibs = a.oibs || (a.oib ? [a.oib] : [])
              oibs.forEach(oib => { if (oib.includes(q)) flat.push({ ...a, oib }) })
            })
            this.results = flat.length ? flat : list

            if (this.results.length === 0 && q.length === 11 && /^\d{11}$/.test(q)) {
              try {
                const bizResp = await callBusinessEntities(q)
                if (bizResp?.status === 200 && bizResp.data?.data?.code === 200 && bizResp.data.data.business_entity) {
                  const e = bizResp.data.data.business_entity
                  this.results = [{
                    id: e.oib || q, oib: e.oib || q, oibs: [e.oib || q],
                    name: e.name || e.shortName || '',
                    address: e.headquatersAddress || '',
                    city: e.headquatersCity || '',
                    zip_code: e.headquatersZip || '',
                    country: 'Hrvatska', status: 'OK'
                  }]
                }
              } catch (err) {}
            }
          } else {
            this.results = list.sort((a, b) => a.name.localeCompare(b.name, 'hr'))
          }
        } else {
          this.results = []
        }
      } catch (e) {
        this.results = []
      } finally {
        this.loading = false
        this.searched = true
      }
    },

    selectResult (r) {
      this.fillForm(r)
      this.results = []
      this.query = r.name || ''
    },

    async onConfirm () {
      if (!this.hasForm) {
        this.$emit('select-customer', null)
        this.$emit('close')
        return
      }

      if (this.oibError) return

      if (this.saveToDatabase && this.form.name) {
        this.saving = true
        try {
          const params = {
            company_id: state.getCurrentCompany().id,
            name: this.form.name.toLowerCase(),
            oibs: this.form.oib ? [this.form.oib.trim()] : [],
            address: (this.form.address || '').toLowerCase(),
            city: (this.form.city || '').toLowerCase(),
            zip_code: this.form.zip_code || '',
            country: this.form.country || 'Hrvatska',
            associate_type: { buyer: true },
            id: uuidv4()
          }
          await callAssociates('post', `/associates/${state.getCurrentCompany().id}/associate`, params)
        } catch (e) {
          console.error('ShopCustomerPanel: greška pri spremanju kupca', e)
        } finally {
          this.saving = false
        }
      }

      this.$emit('select-customer', { ...this.form })
      this.$emit('close')
    },

    onCancel () {
      this.$emit('close')
    }
  },

  beforeDestroy () {
    clearTimeout(this.debounceTimer)
  }
}
</script>

<style scoped>
.modal-header {
  display: flex; align-items: center;
  padding: 20px 20px 14px; border-bottom: 1px solid #F1F5F9;
}
.modal-title { font-size: 15px; font-weight: 700; color: #1e293b; }
.modal-close {
  background: transparent; border: none; cursor: pointer;
  color: #B0BEC5; display: flex; align-items: center;
  padding: 4px; border-radius: 6px; transition: all 0.15s;
}
.modal-close:hover { background: #F1F5F9; color: #374151; }

.modal-body { padding: 16px 20px 8px; display: flex; flex-direction: column; gap: 10px; }

/* Search pill */
.search-pill {
  display: flex; align-items: center;
  padding: 9px 14px; border-radius: 20px;
  border: 1.5px solid #E2E8F0; background: #F8FAFC;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.search-pill.focused { border-color: #00BCD4; background: #fff; box-shadow: 0 0 0 3px rgba(0,188,212,0.1); }
.search-input {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 13px; font-weight: 500; color: #374151; min-width: 0;
}
.search-input::placeholder { color: #9CA3AF; font-weight: 400; }
.search-clear {
  background: transparent; border: none; cursor: pointer;
  color: #B0BEC5; display: flex; align-items: center;
}
.search-clear:hover { color: #374151; }

/* Loading */
.search-loading {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 16px 0 4px;
  font-size: 12px; color: #00BCD4; font-weight: 600;
}

/* Rezultati */
.results-list {
  border: 1px solid #F1F5F9; border-radius: 12px;
  overflow: hidden; max-height: 180px; overflow-y: auto;
  scrollbar-width: thin; scrollbar-color: rgba(0,188,212,0.4) transparent;
}
.results-list::-webkit-scrollbar { width: 4px; }
.results-list::-webkit-scrollbar-thumb { background: rgba(0,188,212,0.4); border-radius: 2px; }
.result-row {
  display: flex; align-items: center;
  padding: 9px 14px; cursor: pointer;
  border-bottom: 1px solid #F8FAFC; transition: background 0.1s;
}
.result-row:last-child { border-bottom: none; }
.result-row:hover { background: rgba(0,188,212,0.05); }
.result-row.selected { background: rgba(0,188,212,0.08); }
.result-info { flex: 1; min-width: 0; }
.result-name { font-size: 13px; font-weight: 700; color: #1e293b; }
.result-oib { font-size: 11px; color: #94A3B8; margin-top: 1px; }

/* Prazna stanja */
.search-empty, .search-hint {
  display: flex; flex-direction: column; align-items: center;
  gap: 4px; padding: 16px 0 4px;
  font-size: 12px; color: #B0BEC5; text-align: center;
}

/* Polja kupca */
.customer-fields {
  border: 1px solid #F1F5F9; border-radius: 12px;
  padding: 12px 14px 14px;
  background: #FAFBFC;
}
.fields-header {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 10px;
}
.fields-title {
  font-size: 11px; font-weight: 700; color: #94A3B8;
  text-transform: uppercase; letter-spacing: 0.6px; flex: 1;
}
.fields-clear {
  display: flex; align-items: center; gap: 3px;
  background: transparent; border: none; cursor: pointer;
  font-size: 11px; color: #E53935; padding: 2px 6px;
  border-radius: 6px; transition: background 0.15s;
}
.fields-clear:hover { background: rgba(229,57,53,0.08); }
.field-input { background: #fff; }
.field-input >>> .v-input__slot { border-radius: 12px !important; background: #F8FAFC !important; }
.field-input >>> fieldset { border-color: #E2E8F0 !important; border-radius: 12px !important; }
.field-input >>> .v-input__slot:hover fieldset { border-color: #00BCD4 !important; }
.field-input >>> .v-input--is-focused fieldset { border-color: #00BCD4 !important; box-shadow: 0 0 0 3px rgba(0,188,212,0.1); }

.save-row { border-top: 1px solid #F1F5F9; padding-top: 10px; }
.save-checkbox { margin: 0 !important; }
.save-label { font-size: 12px; font-weight: 500; color: #64748B; }

/* Footer */
.modal-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 14px 20px 18px; border-top: 1px solid #F1F5F9;
}
.btn-cancel {
  padding: 11px 20px; background: #F8FAFC;
  border: 1.5px solid #E2E8F0; border-radius: 10px;
  font-size: 13px; font-weight: 600; color: #64748B;
  cursor: pointer; transition: all 0.15s; letter-spacing: 0;
}
.btn-cancel:hover { background: #F1F5F9; border-color: #CBD5E1; color: #334155; }
.btn-confirm {
  display: flex; align-items: center; gap: 6px;
  padding: 11px 22px;
  background-color: #00BCD4 !important; background: #00BCD4 !important;
  color: #fff !important; border-radius: 10px; border: none !important;
  font-size: 13px; font-weight: 700; cursor: pointer; letter-spacing: 0;
  box-shadow: 0 2px 8px rgba(0,188,212,0.3); transition: background-color 0.15s;
}
.btn-confirm:hover { background-color: #0097A7 !important; background: #0097A7 !important; }
</style>
